import { useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { AnalyticsRatings, RatedCourse } from '../../types/analytics';
import { useImageUrl } from '../../services/uploads';
import { EASE_OUT } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

type TabKey = 'last_7_days' | 'last_30_days' | 'all_time';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'all_time', label: 'All times' },
];

interface HighestRatedCoursesProps {
  ratings: AnalyticsRatings | undefined;
  loading?: boolean;
}

function CourseItem({ course }: { course: RatedCourse }) {
  const [resolvedImageUrl] = useImageUrl(course.image_url);

  return (
    <div className="flex items-center gap-4">
      {/* Thumbnail */}
      {resolvedImageUrl ? (
        <img
          src={resolvedImageUrl}
          alt=""
          className="w-12 h-12 rounded-xl object-cover shrink-0 bg-neutral-100 dark:bg-grey-800"
        />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-neutral-100 dark:bg-grey-800 shrink-0" />
      )}

      {/* Info */}
      <div className="flex flex-col gap-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">
          {course.title}
        </p>
        <div className="flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-grey-400" />
          <span className="w-1 h-1 rounded-full bg-grey-400" />
          <div className="flex items-center gap-1">
            <StarIcon className="w-5 h-5 text-warning-500" />
            <span className="text-xs text-text-primary">{course.rating}/5</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const PLACEHOLDER_COURSES: RatedCourse[] = Array.from({ length: 5 }, (_, i) => ({
  id: `placeholder-${i}`,
  title: 'Loading course title',
  rating: 0,
  image_url: undefined,
}));

const HighestRatedCourses: React.FC<HighestRatedCoursesProps> = ({ ratings, loading }) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<TabKey>('last_7_days');
  // Widget shows at most 5 (server already caps; slice guards older payloads).
  const courses = (ratings?.[activeTab] ?? []).slice(0, 5);
  const displayCourses = loading && courses.length === 0 ? PLACEHOLDER_COURSES : courses;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.15, ease: EASE_OUT }}
      className="h-full bg-white dark:bg-grey-900 rounded-2xl shadow overflow-hidden flex flex-col"
      data-testid="highest-rated-courses"
    >
      {/* Header */}
      <div className="px-6 py-6">
        <h2 className="text-lg font-semibold text-text-primary">
          Course Ratings by Learners
        </h2>
      </div>

      {/* Tabs */}
      <div className="bg-neutral-100 dark:bg-grey-800">
        <div className="flex p-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{ transition: 'background-color 120ms, color 120ms' }}
              className={`flex-1 text-sm py-2 px-3 rounded-full text-center whitespace-nowrap ${ activeTab === tab.key ? 'bg-primary-500 font-semibold shadow-sm' : 'text-text-secondary font-medium hover:text-text-primary' }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Course list */}
      <div
        className={cn(
          'flex-1 p-6 flex flex-col gap-6',
          displayCourses.length === 0 && 'justify-center',
        )}
      >
        <phantom-ui {...phantomProps(theme === 'dark', loading)}>
          {displayCourses.length > 0 ? (
            <div className="flex flex-col gap-6">
              {displayCourses.map((course) => (
                <CourseItem key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-tertiary text-center py-8">
              No ratings yet
            </p>
          )}
        </phantom-ui>
      </div>
    </motion.div>
  );
};

export default HighestRatedCourses;
